import {
  Component,
  Host,
  h,
  Prop,
  Element,
  State,
  Method,
  getAssetPath
} from "@stencil/core";

import { MercuryBundles, getIconPath } from "@genexus/mercury";
import {
  AccordionModel,
  ComboBoxModel,
  TabularGridSelectionChangedEvent
} from "@genexus/chameleon-controls-library";

import { Locale } from "../../common/locale";
import { config } from "../../common/config";
import { EntityData, GxOption, ObjectType } from "../../common/types";
import { formatDate } from "../../common/helpers";
import {
  mapObjectTypeToComboBoxItemModel,
  mapCategoryToComboBoxItemModel
} from "./helpers";
import { FILTER_AFTER, FilterAfterValue } from "../../common/constants";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/form--full",
  "components/accordion",
  "components/tabular-grid",
  "utils/typography",
  "utils/layout",
  "utils/spacing"
];

const FILTER_ICON = getIconPath({
  category: "window-tools",
  name: "filter",
  colorType: "on-surface"
});
const USER_ICON = getIconPath({
  category: "system",
  name: "user",
  colorType: "on-surface"
});

const MODIFIED_ACCORDION_MODEL: AccordionModel = [
  {
    id: "modified",
    caption: "Modified",
    expanded: false
  }
];

let TODAY: string = null;

@Component({
  tag: "gx-ide-object-selector",
  styleUrl: "object-selector.scss",
  shadow: { delegatesFocus: true },
  assetsDirs: ["gx-ide-assets/object-selector"]
})
export class GxIdeObjectSelector {
  #componentLocale: any;
  #shortcutsSrc = getAssetPath(
    `./gx-ide-assets/object-selector/shortcuts.json`
  );

  #afterModel: ComboBoxModel = null;

  @Element() el: HTMLGxIdeObjectSelectorElement;

  /* References needed to collect data */
  #patternInput!: HTMLChEditElement;
  #typeComboBox!: HTMLChComboBoxRenderElement;
  #categoryComboBox!: HTMLChComboBoxRenderElement;
  #moduleEntitySelector!: HTMLGxIdeEntitySelectorElement;
  #afterTypeComboBox!: HTMLChComboBoxRenderElement;
  #userInput!: HTMLChEditElement;
  #modifiedDateInput!: HTMLInputElement;
  #objectsGrid!: HTMLChTabularGridElement;
  #shortcutsEl: HTMLChShortcutsElement;

  /**
   * For show or hide the filters relative to modifiers
   */
  @State() filterModified = false;

  /**
   * True if loadCallback has been called and has not been resolved yet.
   */
  @State() loading: boolean = false;

  /**
   * The "modified after" value
   */
  @State() modifiedAfterValue: FilterAfterValue;

  /**
   * The objects rendered in the table
   */
  @State() objects: ObjectData[] = [];

  /**
   * The selected objects in the table of objects
   */
  @State() selectedObjectsIdsArray: string[] = [];

  /**
   * Callback invoked when the user wishes to cancel the selection of objects.
   */
  @Prop() readonly cancelCallback!: CancelCallback;

  /**
   * The categories render in the filter category selector
   */
  @Prop() readonly categories!: GxOption[];

  /**
   * The default id value for the category filter
   */
  @Prop() readonly defaultCategory: string;

  /**
   * The default value for the module/folder filter
   */
  @Prop() readonly defaultModule: EntityData;

  /**
   * The default value for the type filter
   */
  @Prop() readonly defaultType: string;

  /**
   * If true, it will display a loader when needed.
   */
  @Prop() readonly loader = false;

  /**
   * Callback invoked when the component needs to reload the list of attributes.
   */
  @Prop() readonly loadCallback!: LoadCallback;

  /**
   * True if multiple object selection is allowed. Default is 'True'
   */
  @Prop() readonly multiSelection: boolean = false;

  /**
   * Callback invoked when the user presses the 'New' button.
   */
  @Prop() readonly newObjectCallback!: NewObjectCallback;

  /**
   * This is a function provided by the developer for expanded the data of object that is double-clicked or entered.
   */
  @Prop() readonly openSelectionCallback!: OpenSelectionCallback;

  /**
   * Current value of the input pattern. It is also used to set the default value when initializing the component
   */
  @Prop() readonly patternValue: string;

  /**
   * Callback invoked when the action is executed in the Module/Folder filter. It returns the information of the selected object (id and name) or 'undefined' if it was canceled.
   */
  @Prop() readonly selectModuleCallback: SelectModuleCallback;

  /**
   * The types render in the filter type selector
   */
  @Prop() readonly types!: ObjectType[];

  async connectedCallback() {
    TODAY = this.#getToday();
  }

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.#setAfterOptions();
    this.#setAfterDefaultValue();
  }

  componentDidLoad() {
    this.#refreshUIWithNewConfiguration();
    this.#patternInput.focus();
  }

  componentDidRender() {}

  /**
   * Suspends or reactivates the shortcuts
   */
  @Method()
  async suspendShortcuts(suspendShortcuts: boolean) {
    if (suspendShortcuts) {
      this.#shortcutsEl.suspend = true;
    } else {
      this.#shortcutsEl.suspend = false;
    }
  }

  /**
   * Validate necessary data input
   */
  @Method()
  async validate(): Promise<boolean> {
    const isValid = true;
    return isValid;
  }

  #cancelCallbackHandler = (): void => {
    this.cancelCallback();
  };

  #chGridKeyDownHandler = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      this.#openSelectionCallbackHandler(e);
    }
  };

  #deselectAll = (): void => {
    (this.#objectsGrid as any).selectAllRows(false);
  };

  #evaluateObjectsCount = (type: "selected" | "matching"): string => {
    const locale = this.#componentLocale;

    if (type === "selected") {
      if (this.selectedObjectsIdsArray.length === 0) {
        return locale.objCount.selected.none;
      }
      if (this.selectedObjectsIdsArray.length === 1) {
        return `1 ${locale.objCount.selected.one}`;
      }
      return `${this.selectedObjectsIdsArray.length} ${locale.objCount.selected.many}`;
    }

    // type === "matching"
    if (this.objects.length === 0) {
      return locale.objCount.matching.none;
    }
    if (this.objects.length === 1) {
      return `1 ${locale.objCount.matching.one}`;
    }
    return `${this.objects.length} ${locale.objCount.matching.many}`;
  };

  #getToday = (): string => {
    const now = new Date();
    const date = now.toISOString().split("T")[0];
    const time = now.toISOString().split("T")[1].slice(0, 5); // Only take 'HH:MM' part
    return `${date}T${time}`;
  };

  #handleAfterValueChange = (event: CustomEvent<string> | InputEvent) => {
    this.modifiedAfterValue = event.detail as FilterAfterValue;
    if (
      this.modifiedAfterValue !== FILTER_AFTER.DATE_TIME ||
      (this.modifiedAfterValue === FILTER_AFTER.DATE_TIME &&
        this.#modifiedDateInput.value)
    ) {
      // if "modified" is "after date/time" only refresh ui if date/time is valid.
      this.#refreshUIWithNewConfiguration();
    }
  };

  #handleObjectsSelectionChange = (
    event: CustomEvent<TabularGridSelectionChangedEvent>
  ) => {
    this.selectedObjectsIdsArray = event.detail.rowsId;
  };

  #hostKeyPressHandler = (e: KeyboardEvent) => {
    // just prevent keypress propagation
    if (e.key === "Enter") {
      e.stopPropagation();
    }
  };

  #newObjectCallbackHandler = (): void => {
    this.newObjectCallback();
  };

  #openSelectionCallbackHandler = (e: MouseEvent | KeyboardEvent): void => {
    e.stopPropagation();
    this.openSelectionCallback(this.selectedObjectsIdsArray);
  };

  #refreshUIWithNewConfiguration = (): void => {
    /* 'modified' checkbox, should only be considered if 'date' || 'user' */
    const modifiedAfter = this.#afterTypeComboBox.value;
    const user = this.#userInput?.value?.trim();
    const filters: ObjectFiltersData = {
      pattern: this.#patternInput?.value,
      type: this.#typeComboBox.value,
      category: this.#categoryComboBox.value,
      module: this.#moduleEntitySelector?.value?.id,
      modifiedAfter: modifiedAfter ? modifiedAfter : null,
      modifiedBy: user ? user : null,
      modifiedDate:
        modifiedAfter === FILTER_AFTER.DATE_TIME
          ? new Date(this.#modifiedDateInput?.value)
          : null
    };
    this.loading = true;
    this.objects = [];
    this.loadCallback(filters).then((objects: ObjectData[]) => {
      if (objects) {
        this.objects = this.#sortObjectsByName(objects);
      }
      this.selectedObjectsIdsArray = [];
      this.#deselectAll();
      this.loading = false;
    });
  };

  #renderFilter = (): Element => {
    return (
      <header class="dialog-header spacing-body-inline spacing-body-block-start">
        <form class="field-group filter-layout">
          <div class="field field-block pattern">
            <label
              // pattern
              class="label"
              htmlFor="pattern"
            >
              {this.#componentLocale.filter.pattern}
            </label>

            <ch-edit
              accessibleName={this.#componentLocale.filter.pattern}
              debounce={config.inputDebounce}
              class="input"
              part="filter-pattern"
              id="pattern"
              name="pattern"
              value={this.patternValue}
              mode="text"
              ref={(el: HTMLChEditElement) =>
                (this.#patternInput = el as HTMLChEditElement)
              }
              onInput={this.#refreshUIWithNewConfiguration}
            ></ch-edit>
          </div>

          <div class="field field-block type">
            <label
              // type
              class="label"
              htmlFor="type"
            >
              {this.#componentLocale.filter.type}
            </label>
            <ch-combo-box-render
              accessibleName={this.#componentLocale.filter.type}
              class="combo-box"
              disabled={!this.types}
              value={this.defaultType}
              part="filter-type"
              id="type"
              model={mapObjectTypeToComboBoxItemModel(this.types)}
              ref={(el: HTMLChComboBoxRenderElement) =>
                (this.#typeComboBox = el as HTMLChComboBoxRenderElement)
              }
              onInput={this.#refreshUIWithNewConfiguration}
            ></ch-combo-box-render>
          </div>

          <div class="field field-block category">
            <label
              // category
              class="label"
              htmlFor="category"
            >
              {this.#componentLocale.filter.category}
            </label>
            <ch-combo-box-render
              id="category"
              class="combo-box"
              accessibleName={this.#componentLocale.filter.category}
              disabled={!this.categories}
              model={mapCategoryToComboBoxItemModel(this.categories)}
              value={this.defaultCategory}
              part="filter-category"
              onInput={this.categories && this.#refreshUIWithNewConfiguration}
              ref={(el: HTMLChComboBoxRenderElement) =>
                (this.#categoryComboBox = el as HTMLChComboBoxRenderElement)
              }
            ></ch-combo-box-render>
          </div>

          <gx-ide-entity-selector
            // module
            class="module"
            id="module"
            value={this.defaultModule}
            ref={(el: HTMLGxIdeEntitySelectorElement) =>
              (this.#moduleEntitySelector =
                el as HTMLGxIdeEntitySelectorElement)
            }
            labelCaption={this.#componentLocale.filter.module}
            labelPosition="block-start"
            onValueChanged={this.#refreshUIWithNewConfiguration}
            defaultValue={this.defaultModule}
            selectEntityCallback={this.selectModuleCallback}
          ></gx-ide-entity-selector>

          <ch-accordion-render
            // modified
            class="accordion-outlined modified-accordion"
            model={MODIFIED_ACCORDION_MODEL}
          >
            <div slot="modified" class="spacing-body">
              <div class="field-group field-group-modified">
                <div class="field field-block after">
                  <label
                    // after
                    class="label"
                    htmlFor="after-type"
                  >
                    {this.#componentLocale.filter.after}
                  </label>
                  <ch-combo-box-render
                    class="combo-box"
                    value={this.modifiedAfterValue}
                    part="filter-after-type"
                    id="after-type"
                    model={this.#afterModel}
                    ref={(el: HTMLChComboBoxRenderElement) =>
                      (this.#afterTypeComboBox =
                        el as HTMLChComboBoxRenderElement)
                    }
                    onInput={this.#handleAfterValueChange}
                  ></ch-combo-box-render>
                </div>

                <div class="field field-block">
                  <label
                    // user
                    class="label"
                    htmlFor="user-name"
                  >
                    {this.#componentLocale.filter.user}
                  </label>
                  <ch-edit
                    class="input"
                    part="filter-user"
                    value={this.patternValue}
                    ref={(el: HTMLChEditElement) =>
                      (this.#userInput = el as HTMLChEditElement)
                    }
                    id="user-name"
                    name="userName"
                    startImgSrc={USER_ICON}
                    onInput={this.#refreshUIWithNewConfiguration}
                  ></ch-edit>
                </div>

                <div class="field field-block">
                  <label
                    // date
                    class={{
                      "label": true,
                      "label--disabled":
                        this.modifiedAfterValue !== "afterDateTime"
                    }}
                    htmlFor="modified-date"
                  >
                    {this.#componentLocale.filter.dateTime}
                  </label>

                  <input
                    type="datetime-local"
                    class="input input-date-time"
                    id="modified-date"
                    name="modifiedDate"
                    part="filter-pattern"
                    value={this.patternValue}
                    max={TODAY}
                    ref={(el: HTMLInputElement) =>
                      (this.#modifiedDateInput = el as HTMLInputElement)
                    }
                    disabled={this.modifiedAfterValue !== "afterDateTime"}
                    onInput={this.#refreshUIWithNewConfiguration}
                  ></input>
                </div>
              </div>
            </div>
          </ch-accordion-render>
        </form>
      </header>
    );
  };

  #renderFooter = (): Element => {
    return (
      <footer class="footer dialog-footer-space-between">
        <div
          class="dialog-footer dialog-footer-space-between objects-count spacing-body-inline"
          slot="footer-above"
        >
          <p class="body-regular-s objects-selected" part="objects-selected">
            {this.#evaluateObjectsCount("selected")}
          </p>
          <p class="body-regular-s objects-matching" part="objects-matching">
            {this.#evaluateObjectsCount("matching")}
          </p>
        </div>

        <div class="dialog-footer-with-border dialog-footer-space-between spacing-body-inline spacing-body-block-end">
          <button
            // button new object
            class="button-secondary"
            part="button button-new"
            onClick={!this.loading ? this.#newObjectCallbackHandler : undefined}
            disabled={this.loading}
          >
            {this.#componentLocale.footer.btnNew}
          </button>
          <div class="end buttons-spacer">
            <button
              // button cancel
              class="button-secondary"
              part="button button-cancel"
              onClick={this.#cancelCallbackHandler}
            >
              {this.#componentLocale.footer.btnCancel}
            </button>
            {this.multiSelection ? (
              <button
                class="button-secondary"
                onClick={this.#selectAll}
                part="button button-select-all"
              >
                {this.#componentLocale.footer.btnSelectAll}
              </button>
            ) : null}
            <button
              // button confirm
              class="button-primary"
              part="button button-confirm"
              onClick={this.#openSelectionCallbackHandler}
              disabled={this.loading}
            >
              {this.#componentLocale.footer.btnConfirm}
            </button>
          </div>
        </div>
      </footer>
    );
  };

  #renderObjects = (): Element => {
    return (
      <ch-tabular-grid
        class={{
          "tabular-grid": true,
          "tabular-grid-object-selector": true,
          "empty-result": this.objects.length === 0
        }}
        rowSelectionMode={this.multiSelection ? "multiple" : "single"}
        ref={(el: HTMLChTabularGridElement) => (this.#objectsGrid = el)}
        part="ch-grid-objects"
        onKeyDown={this.#chGridKeyDownHandler}
        onSelectionChanged={this.#handleObjectsSelectionChange}
        onDblClick={this.#openSelectionCallbackHandler}
      >
        <ch-tabular-grid-columnset class="tabular-grid-column-set">
          <ch-tabular-grid-column
            column-name-position="text"
            settingable={false}
            sortable={false}
            size={config.tabularGrid.colSize.maxContent}
            class="tabular-grid-column"
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            column-name={this.#componentLocale.tableHead.name}
            column-name-position="text"
            settingable={false}
            sortable={false}
            size={config.tabularGrid.colSize.maxContent}
            class="tabular-grid-column"
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            column-name={this.#componentLocale.tableHead.type}
            column-name-position="text"
            settingable={false}
            sortable={false}
            size={config.tabularGrid.colSize.maxContent}
            class="tabular-grid-column"
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            column-name={this.#componentLocale.tableHead.module}
            column-name-position="text"
            settingable={false}
            sortable={false}
            size={config.tabularGrid.colSize.maxContent}
            class="tabular-grid-column"
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            column-name={this.#componentLocale.tableHead.description}
            column-name-position="text"
            settingable={false}
            sortable={false}
            size={config.tabularGrid.colSize.commonDouble}
            class="tabular-grid-column"
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            column-name={this.#componentLocale.tableHead.modifiedDate}
            column-name-position="text"
            settingable={false}
            sortable={false}
            size={config.tabularGrid.colSize.maxContent}
            class="tabular-grid-column"
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            column-name={this.#componentLocale.tableHead.importDate}
            column-name-position="text"
            settingable={false}
            sortable={false}
            size={config.tabularGrid.colSize.maxContent}
            class="tabular-grid-column"
          ></ch-tabular-grid-column>
        </ch-tabular-grid-columnset>

        {this.objects.length ? (
          <ch-tabular-grid-rowset class="tabular-grid-rowset">
            {this.objects.map((obj: ObjectData) => (
              <ch-tabular-grid-row
                rowid={obj.id}
                key={obj.id}
                class="tabular-grid-row"
              >
                <ch-tabular-grid-cell class="tabular-grid-cell">
                  <ch-image src={obj.icon} class="icon-s"></ch-image>
                </ch-tabular-grid-cell>
                <ch-tabular-grid-cell class="tabular-grid-cell">
                  {obj.name}
                </ch-tabular-grid-cell>
                <ch-tabular-grid-cell class="tabular-grid-cell">
                  {obj.type}
                </ch-tabular-grid-cell>
                <ch-tabular-grid-cell class="tabular-grid-cell">
                  {obj.module}
                </ch-tabular-grid-cell>
                <ch-tabular-grid-cell class="tabular-grid-cell">
                  {obj.description}
                </ch-tabular-grid-cell>
                <ch-tabular-grid-cell class="tabular-grid-cell">
                  {`${formatDate(obj.modifiedDate, "date-time")}`}
                </ch-tabular-grid-cell>
                <ch-tabular-grid-cell class="tabular-grid-cell">
                  {`${formatDate(obj.importDate, "date-time")}`}
                </ch-tabular-grid-cell>
              </ch-tabular-grid-row>
            ))}
          </ch-tabular-grid-rowset>
        ) : (
          <ch-tabular-grid-rowset class="tabular-grid-rowset">
            <ch-tabular-grid-rowset-empty>
              {this.loader && this.loading ? (
                <gx-ide-loader
                  loaderTitle={this.#componentLocale.loader.title}
                  show
                ></gx-ide-loader>
              ) : (
                <gx-ide-empty-state
                  stateIconSrc={FILTER_ICON}
                  stateTitle={this.#componentLocale.emptyState.title}
                ></gx-ide-empty-state>
              )}
            </ch-tabular-grid-rowset-empty>
          </ch-tabular-grid-rowset>
        )}
      </ch-tabular-grid>
    );
  };

  #selectAll = (): void => {
    (this.#objectsGrid as any).selectAllRows();
  };

  #setAfterDefaultValue = (): void => {
    this.modifiedAfterValue = FILTER_AFTER.NONE;
  };

  #setAfterOptions = (): void => {
    this.#afterModel = [
      {
        caption: this.#componentLocale.filter.afterNone,
        value: FILTER_AFTER.NONE
      },
      {
        caption: this.#componentLocale.filter[FILTER_AFTER.DATE_TIME],
        value: FILTER_AFTER.DATE_TIME
      },
      {
        caption: this.#componentLocale.filter[FILTER_AFTER.IMPORT],
        value: FILTER_AFTER.IMPORT
      },
      {
        caption: this.#componentLocale.filter[FILTER_AFTER.LAST_BUILD],
        value: FILTER_AFTER.LAST_BUILD
      }
    ];
  };

  #sortObjectsByName = (objects: ObjectData[]): ObjectData[] => {
    return objects.sort((a, b) => {
      const nameA = a.name.toLowerCase(),
        nameB = b.name.toLowerCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });
  };

  render(): void {
    return (
      <Host onKeyPress={this.#hostKeyPressHandler} class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts
          src={this.#shortcutsSrc}
          ref={(el: HTMLChShortcutsElement) =>
            (this.#shortcutsEl = el as HTMLChShortcutsElement)
          }
        ></ch-shortcuts>
        <section class="section">
          {this.#renderFilter()}
          {this.#renderObjects()}
          {this.#renderFooter()}
        </section>
      </Host>
    );
  }
}

export type NewObjectCallback = () => Promise<void>;

export type CancelCallback = () => Promise<void>;

export type SelectModuleCallback = () => Promise<EntityData | undefined>;

export type OpenSelectionCallback = (ids: string[]) => Promise<void>;

export type LoadCallback = (
  filters: ObjectFiltersData
) => Promise<ObjectData[]>;

export type ObjectFiltersData = {
  pattern?: string;
  type: string;
  category?: string;
  module?: string;
  modifiedAfter?: string;
  modifiedBy?: string;
  modifiedDate?: Date;
};

export type ObjectData = {
  id: string;
  icon: string;
  name: string;
  type: string;
  description: string;
  module: string;
  modifiedDate?: Date;
  importDate?: Date;
};
